import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import {
  Calendar, Clock, Mail, FileText, Plus, Play, Pause, Trash2,
  RefreshCw, Download, CheckCircle, AlertCircle, Settings,
  FileBarChart, Shield, AlertTriangle, BarChart3, Users,
  Send, Edit, Eye, History, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Report type configurations
const REPORT_TYPES = {
  comprehensive: { label: 'Comprehensive Report', icon: FileBarChart, color: 'blue' },
  summary: { label: 'Executive Summary', icon: FileText, color: 'purple' },
  violations: { label: 'Violations Report', icon: AlertTriangle, color: 'red' },
  compliance: { label: 'Compliance Status', icon: Shield, color: 'green' },
  training: { label: 'Training Report', icon: Users, color: 'orange' },
  observations: { label: 'Observations Report', icon: Eye, color: 'cyan' },
  audits: { label: 'Audits Report', icon: BarChart3, color: 'indigo' },
  incidents: { label: 'Incidents Report', icon: AlertCircle, color: 'amber' },
  ai_hazard_scans: { label: 'AI Hazard Scan Report', icon: Shield, color: 'violet', description: 'Automated AI photo analysis summary' }
};

const FREQUENCIES = {
  daily: { label: 'Daily', description: 'Every day' },
  weekly: { label: 'Weekly', description: 'Once a week' },
  biweekly: { label: 'Bi-Weekly', description: 'Every two weeks' },
  monthly: { label: 'Monthly', description: 'Once a month' },
  quarterly: { label: 'Quarterly', description: 'Every three months' }
};

const EXPORT_FORMATS = {
  pdf: { label: 'PDF', description: 'Best for printing' },
  excel: { label: 'Excel', description: 'Best for data analysis' },
  csv: { label: 'CSV', description: 'Universal format' },
  html: { label: 'HTML', description: 'Web format with styling' }
};

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' }
];

const ScheduledReportsPage = () => {
  const [schedules, setSchedules] = useState([]);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [stats, setStats] = useState({ total_schedules: 0, active: 0, paused: 0, total_runs: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [historyReports, setHistoryReports] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    report_type: 'comprehensive',
    frequency: 'weekly',
    export_format: 'pdf',
    recipients: '',
    include_charts: true,
    include_recommendations: true,
    day_of_week: 0,
    day_of_month: 1,
    time_of_day: '09:00'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const [schedulesRes, statsRes, reportsRes] = await Promise.all([
        axios.get(`${API_URL}/api/scheduled-reports`, { headers }),
        axios.get(`${API_URL}/api/scheduled-reports/stats/summary`, { headers }),
        axios.get(`${API_URL}/api/scheduled-reports/reports/all?limit=10`, { headers })
      ]);
      
      setSchedules(schedulesRes.data.schedules || []);
      setStats(statsRes.data);
      setGeneratedReports(reportsRes.data.reports || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load scheduled reports",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateSchedule = async () => {
    try {
      setActionLoading('create');
      const headers = getAuthHeaders();
      
      const recipients = formData.recipients
        .split(',')
        .map(e => e.trim())
        .filter(e => e.includes('@'));
      
      if (recipients.length === 0) {
        toast({
          title: "Error",
          description: "Please enter at least one valid email address",
          variant: "destructive"
        });
        return;
      }
      
      const payload = {
        ...formData,
        recipients,
        day_of_week: formData.frequency === 'weekly' || formData.frequency === 'biweekly' 
          ? parseInt(formData.day_of_week) : null,
        day_of_month: formData.frequency === 'monthly' || formData.frequency === 'quarterly'
          ? parseInt(formData.day_of_month) : null
      };
      
      await axios.post(`${API_URL}/api/scheduled-reports`, payload, { headers });
      
      toast({
        title: "Success",
        description: "Report schedule created successfully"
      });
      
      setShowCreateDialog(false);
      setFormData({
        name: '',
        report_type: 'comprehensive',
        frequency: 'weekly',
        export_format: 'pdf',
        recipients: '',
        include_charts: true,
        include_recommendations: true,
        day_of_week: 0,
        day_of_month: 1,
        time_of_day: '09:00'
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create schedule",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRunNow = async (scheduleId) => {
    try {
      setActionLoading(scheduleId);
      const headers = getAuthHeaders();
      
      await axios.post(`${API_URL}/api/scheduled-reports/${scheduleId}/run`, {}, { headers });
      
      toast({
        title: "Success",
        description: "Report generated successfully"
      });
      
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to run report",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (schedule) => {
    try {
      setActionLoading(schedule.id);
      const headers = getAuthHeaders();
      
      const newStatus = schedule.status === 'active' ? 'paused' : 'active';
      
      await axios.put(`${API_URL}/api/scheduled-reports/${schedule.id}`, 
        { status: newStatus }, 
        { headers }
      );
      
      toast({
        title: "Success",
        description: `Schedule ${newStatus === 'active' ? 'activated' : 'paused'}`
      });
      
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update schedule",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      setActionLoading(scheduleId);
      const headers = getAuthHeaders();
      
      await axios.delete(`${API_URL}/api/scheduled-reports/${scheduleId}`, { headers });
      
      toast({
        title: "Success",
        description: "Schedule deleted successfully"
      });
      
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete schedule",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewHistory = async (schedule) => {
    try {
      setSelectedSchedule(schedule);
      const headers = getAuthHeaders();
      
      const response = await axios.get(
        `${API_URL}/api/scheduled-reports/${schedule.id}/history`, 
        { headers }
      );
      
      setHistoryReports(response.data.reports || []);
      setShowHistoryDialog(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load report history",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-blue-100 text-blue-700'
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  const formatNextRun = (nextRun) => {
    if (!nextRun) return 'Not scheduled';
    const date = new Date(nextRun);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <PageHeader
          title="Scheduled Reports & Exports"
          subtitle="Automate your safety reports with scheduled email delivery and downloads"
          icon={Calendar}
          variant="blue"
          onRefresh={fetchData}
          loading={loading}
          actions={
            <Button 
              onClick={() => setShowCreateDialog(true)}
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Schedule
            </Button>
          }
          stats={[
            { icon: Calendar, label: 'Total Schedules', value: stats.total_schedules, color: 'blue' },
            { icon: CheckCircle, label: 'Active', value: stats.active, color: 'green' },
            { icon: Pause, label: 'Paused', value: stats.paused, color: 'yellow' },
            { icon: Send, label: 'Reports Sent', value: stats.total_runs, color: 'purple' }
          ]}
        />

        <Tabs defaultValue="schedules" className="space-y-4">
          <TabsList>
            <TabsTrigger value="schedules">Schedules</TabsTrigger>
            <TabsTrigger value="history">Report History</TabsTrigger>
          </TabsList>

          <TabsContent value="schedules" className="space-y-4">
            {schedules.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Calendar className="w-16 h-16 text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No Scheduled Reports</h3>
                  <p className="text-gray-500 text-center mb-4">
                    Create your first scheduled report to automate report generation and delivery.
                  </p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Schedule
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {schedules.map((schedule) => {
                  const reportType = REPORT_TYPES[schedule.report_type] || REPORT_TYPES.comprehensive;
                  const ReportIcon = reportType.icon;
                  
                  return (
                    <Card key={schedule.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg bg-${reportType.color}-100`}>
                              <ReportIcon className={`w-5 h-5 text-${reportType.color}-600`} />
                            </div>
                            <div>
                              <CardTitle className="text-base">{schedule.name}</CardTitle>
                              <CardDescription>{reportType.label}</CardDescription>
                            </div>
                          </div>
                          {getStatusBadge(schedule.status)}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{FREQUENCIES[schedule.frequency]?.label || schedule.frequency}</span>
                          <span className="text-gray-400">•</span>
                          <span>{schedule.time_of_day}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4" />
                          <span>{schedule.recipients?.length || 0} recipients</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FileText className="w-4 h-4" />
                          <span>{schedule.export_format?.toUpperCase()}</span>
                          <span className="text-gray-400">•</span>
                          <span>{schedule.run_count || 0} reports sent</span>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          Next run: {formatNextRun(schedule.next_run)}
                        </div>
                        
                        <div className="flex gap-2 pt-2 border-t">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleRunNow(schedule.id)}
                            disabled={actionLoading === schedule.id}
                          >
                            {actionLoading === schedule.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleToggleStatus(schedule)}
                          >
                            {schedule.status === 'active' ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewHistory(schedule)}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDelete(schedule.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Recent Generated Reports</CardTitle>
                <CardDescription>Download or view your previously generated reports</CardDescription>
              </CardHeader>
              <CardContent>
                {generatedReports.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No reports generated yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {generatedReports.map((report) => (
                      <div 
                        key={report.id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium">{report.title}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(report.generated_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {report.email_sent && (
                            <Badge className="bg-green-100 text-green-700">
                              <Mail className="w-3 h-3 mr-1" />
                              Sent
                            </Badge>
                          )}
                          <Button variant="outline" size="sm">
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Schedule Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Scheduled Report</DialogTitle>
              <DialogDescription>
                Set up automatic report generation and email delivery
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Schedule Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Monthly Safety Review"
                  />
                </div>
                
                <div>
                  <Label>Report Type</Label>
                  <Select
                    value={formData.report_type}
                    onValueChange={(value) => setFormData({ ...formData, report_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(REPORT_TYPES).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Frequency</Label>
                  <Select
                    value={formData.frequency}
                    onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(FREQUENCIES).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {(formData.frequency === 'weekly' || formData.frequency === 'biweekly') && (
                  <div>
                    <Label>Day of Week</Label>
                    <Select
                      value={String(formData.day_of_week)}
                      onValueChange={(value) => setFormData({ ...formData, day_of_week: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map((day) => (
                          <SelectItem key={day.value} value={String(day.value)}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {(formData.frequency === 'monthly' || formData.frequency === 'quarterly') && (
                  <div>
                    <Label>Day of Month</Label>
                    <Select
                      value={String(formData.day_of_month)}
                      onValueChange={(value) => setFormData({ ...formData, day_of_month: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                          <SelectItem key={day} value={String(day)}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label>Time of Day</Label>
                  <Input
                    type="time"
                    value={formData.time_of_day}
                    onChange={(e) => setFormData({ ...formData, time_of_day: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label>Export Format</Label>
                  <Select
                    value={formData.export_format}
                    onValueChange={(value) => setFormData({ ...formData, export_format: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EXPORT_FORMATS).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2">
                  <Label>Recipients (comma-separated emails)</Label>
                  <Input
                    value={formData.recipients}
                    onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
                    placeholder="safety@company.com, manager@company.com"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label>Include Charts</Label>
                  <Switch
                    checked={formData.include_charts}
                    onCheckedChange={(checked) => setFormData({ ...formData, include_charts: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label>Include Recommendations</Label>
                  <Switch
                    checked={formData.include_recommendations}
                    onCheckedChange={(checked) => setFormData({ ...formData, include_recommendations: checked })}
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateSchedule}
                disabled={!formData.name || !formData.recipients || actionLoading === 'create'}
              >
                {actionLoading === 'create' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Report History: {selectedSchedule?.name}</DialogTitle>
              <DialogDescription>
                View and download previously generated reports
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {historyReports.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No reports generated yet for this schedule
                </div>
              ) : (
                historyReports.map((report) => (
                  <div 
                    key={report.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{report.title}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(report.generated_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {report.email_sent && (
                        <Badge className="bg-green-100 text-green-700">
                          Emailed
                        </Badge>
                      )}
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ScheduledReportsPage;
